import React, { useState, useEffect, useRef, useCallback } from "react";
import { connect } from "react-redux";
import { Popup, Button } from "devextreme-react";
import FormField from "../../../../../dynamicFormBuilder/FormField";
import { getCardConnectPayLayout, CardConnectPaymentMethods, CARDCONNECT_NEW_PROFILE } from "./CardConnectPayLayout";
import "./cardConnectPay.css";

const CARDCONNECT_LAYOUT_ID = "C24222C7-F580-4A28-B585-FE6F81C1047B";
const CARDCONNECT_LAYOUT_NAME = "CardConnectPayment";

/** Delphi uOMD0043 FormShow / PopulateFields: a customer with a payment profile opens on "Pay using Profile",
 *  one without on "Pay using Card". Account always starts empty. */
function initialFormFields(showPopup) {
    const paymentProfile = (showPopup.paymentProfile || "").trim();
    return {
        CARDCONNECT_PAYMENT_METHOD: paymentProfile ? CardConnectPaymentMethods.Profile : CardConnectPaymentMethods.Card,
        CARDCONNECT_TRANS_NO: showPopup.transactionNumber ?? "",
        CARDCONNECT_CUSTOMER: showPopup.customer ?? "",
        CARDCONNECT_AMOUNT: showPopup.amount != null && showPopup.amount !== "" ? Number(showPopup.amount) : null,
        CARDCONNECT_PROFILE: paymentProfile,
        CARDCONNECT_ACCOUNT: "",
    };
}

/** Delphi btnOKClick: ProfileToUse is "profile/account" - the separator is sent even when Account is blank - and
 *  NewProfile is "New"; paying by card sends both empty. */
function buildPayArguments(fields) {
    const method = fields.CARDCONNECT_PAYMENT_METHOD;
    if (method === CardConnectPaymentMethods.Profile) {
        const profile = (fields.CARDCONNECT_PROFILE || "").trim();
        const account = (fields.CARDCONNECT_ACCOUNT || "").trim();
        return { profileToUse: `${profile}/${account}`, newProfile: "" };
    }
    if (method === CardConnectPaymentMethods.NewProfile) {
        return { profileToUse: "", newProfile: CARDCONNECT_NEW_PROFILE };
    }
    return { profileToUse: "", newProfile: "" };
}

/* Mounted afresh on every open. FormField reads its layout once, when it mounts, so the layout and the
   starting values have to be right on the first render - not patched in by an effect afterwards, which left
   the radio options enabled/disabled for the previously opened order (or for no profile at all). */
function CardConnectPayDialog({ showPopup, currentUser, onConfirm, onClose }) {
    const formRefs = useRef({});
    const [defaultLayout] = useState(() => getCardConnectPayLayout(!!(showPopup.paymentProfile || "").trim()));
    const [popupFormFields, setPopupFormFields] = useState(() => initialFormFields(showPopup));
    const [formLoaded, setFormLoaded] = useState(false);
    const method = popupFormFields.CARDCONNECT_PAYMENT_METHOD;

    const onFormLoad = useCallback(() => setFormLoaded(true), []);

    /* Delphi rdProfileClick / EnableDisableControl: Account is editable only while paying by profile. Applied
       once FormField has rendered its fields - before that there is no editor to reach. */
    useEffect(() => {
        if (!formLoaded) return;
        const inst = formRefs.current?.CARDCONNECT_ACCOUNT?.instance?.();
        if (inst) {
            inst.option("readOnly", method !== CardConnectPaymentMethods.Profile);
        }
    }, [formLoaded, method]);

    /* Delphi rdProfileClick clears Account on every change of payment method. */
    const handleFieldValueChange = useCallback((name) => {
        if (name === "CARDCONNECT_PAYMENT_METHOD") {
            setPopupFormFields((prev) => ({ ...prev, CARDCONNECT_ACCOUNT: "" }));
        }
    }, []);

    const handleOk = useCallback(() => {
        const { profileToUse, newProfile } = buildPayArguments(popupFormFields);
        onClose();
        if (onConfirm) {
            onConfirm(showPopup.headerRowId, profileToUse, newProfile);
        }
    }, [onConfirm, onClose, showPopup.headerRowId, popupFormFields]);

    return (
        <Popup
            visible={true}
            onHiding={onClose}
            dragEnabled={false}
            hideOnOutsideClick={false}
            showCloseButton={true}
            showTitle={true}
            title="CardConnect Payment"
            width={720}
            maxWidth="95vw"
            height="auto"
        >
            <div className="cardconnect-pay">
                <FormField
                    currrentUser={currentUser}
                    layoutId={CARDCONNECT_LAYOUT_ID}
                    layoutName={CARDCONNECT_LAYOUT_NAME}
                    defaultFormSchema={defaultLayout}
                    formFields={popupFormFields}
                    setformFields={setPopupFormFields}
                    handleFieldValueChange={handleFieldValueChange}
                    onFormLoad={onFormLoad}
                    DYNAMICFORMSETTING="No"
                    formRefs={formRefs}
                    hasOnlyOneColumn={true}
                />
                <div className="cardconnect-pay-footer">
                    <Button text="OK" type="default" icon="check" onClick={handleOk} />
                </div>
            </div>
        </Popup>
    );
}

/** CardConnect Payment (Delphi uOMD0043). Form.js verifies the order and loads it from the server
 *  (prepareCardConnectPayOpen) before opening; OK hands the profile choice to `onConfirm`. */
function CardConnectPayPopup(props) {
    const { showPopup = {}, setShowPopup, onConfirm } = props;

    const handleClose = useCallback(() => {
        setShowPopup((prev) => ({ ...prev, visible: false }));
    }, [setShowPopup]);

    if (showPopup.visible !== true || !showPopup.headerRowId) return null;

    return (
        <CardConnectPayDialog
            showPopup={showPopup}
            currentUser={props.oidc?.user}
            onConfirm={onConfirm}
            onClose={handleClose}
        />
    );
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(CardConnectPayPopup);
